import { getJson } from "@/lib/api"
import type {
  ConceptResponse,
  GraphEdge,
  GraphNode,
  GraphPayload,
  GraphStats,
  PathResponse,
  SearchResponse,
} from "@/types/knowledgeGraph"

const BASE = "/api/knowledge_graph"

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === "") return
    search.set(key, String(value))
  })
  const serialized = search.toString()
  return serialized ? `?${serialized}` : ""
}

/** The API reads edge_types as a comma-separated list; an empty list means its default. */
function edgeTypeParam(edgeTypes?: string[]): string | undefined {
  return edgeTypes && edgeTypes.length > 0 ? edgeTypes.join(",") : undefined
}

export interface TraversalOptions {
  hops?: number
  edgeTypes?: string[]
}

export function searchConcepts(
  term: string,
  options: TraversalOptions & { mode?: "name" | "semantic" } = {}
): Promise<SearchResponse> {
  return getJson<SearchResponse>(
    `${BASE}/search${query({
      q: term,
      mode: options.mode,
      hops: options.hops,
      edge_types: edgeTypeParam(options.edgeTypes),
    })}`
  )
}

export function fetchConcept(slug: string, options: TraversalOptions = {}): Promise<ConceptResponse> {
  return getJson<ConceptResponse>(
    `${BASE}/concept${query({ slug, hops: options.hops, edge_types: edgeTypeParam(options.edgeTypes) })}`
  )
}

export function fetchNeighborhood(slug: string, options: TraversalOptions = {}): Promise<GraphPayload> {
  return getJson<GraphPayload>(
    `${BASE}/neighborhood${query({
      slug,
      hops: options.hops ?? 1,
      edge_types: edgeTypeParam(options.edgeTypes),
    })}`
  )
}

export function fetchPath(from: string, to: string): Promise<PathResponse> {
  return getJson<PathResponse>(`${BASE}/path${query({ from, to })}`)
}

export function fetchStats(): Promise<GraphStats> {
  return getJson<GraphStats>(`${BASE}/stats`)
}

export const EMPTY_GRAPH: GraphPayload = { root_ids: [], nodes: [], edges: [] }

/**
 * Merges an expansion into the graph already on screen.
 *
 * Expanding a node should add to the canvas rather than replace it -- the whole
 * point of the explorer is following a chain -- so nodes and edges are deduplicated
 * by id and existing entries win, keeping any `root` flag the user is oriented by.
 */
export function mergeGraphs(current: GraphPayload, addition: GraphPayload): GraphPayload {
  const nodes = new Map<number, GraphNode>()
  current.nodes.forEach((node) => nodes.set(node.id, node))
  addition.nodes.forEach((node) => {
    const existing = nodes.get(node.id)
    nodes.set(node.id, existing ? { ...node, root: existing.root || node.root } : node)
  })

  const edges = new Map<number, GraphEdge>()
  current.edges.forEach((edge) => edges.set(edge.id, edge))
  addition.edges.forEach((edge) => edges.set(edge.id, edge))

  const rootIds = Array.from(new Set([...current.root_ids, ...addition.root_ids]))

  return { root_ids: rootIds, nodes: Array.from(nodes.values()), edges: Array.from(edges.values()) }
}

/**
 * Hides nodes the viewer has toggled off, and any edge left dangling as a result.
 *
 * Applied to the loaded payload rather than re-fetched: the data is already here, and
 * a visibility toggle that costs a round trip does not feel like a toggle. Kinds and
 * documents are filtered here; hops and edge types are the server's business, since
 * they decide what gets traversed in the first place.
 */
export function filterGraph(
  graph: GraphPayload,
  options: { kinds?: string[]; showDocuments?: boolean } = {}
): GraphPayload {
  const kinds = options.kinds ?? []
  const showDocuments = options.showDocuments ?? true
  if (kinds.length === 0 && showDocuments) return graph

  const nodes = graph.nodes.filter((node) => {
    if (node.label === "Document") return showDocuments
    if (node.label !== "Concept") return true
    // Roots stay visible: hiding the concept the view is centred on would leave the
    // canvas showing a neighbourhood with no middle.
    if (node.root) return true
    return kinds.length === 0 || kinds.includes(node.kind ?? "topic")
  })

  const visible = new Set(nodes.map((node) => node.id))
  const edges = graph.edges.filter((edge) => visible.has(edge.from) && visible.has(edge.to))

  return { root_ids: graph.root_ids.filter((id) => visible.has(id)), nodes, edges }
}

export function nodeLabel(node: GraphNode): string {
  return node.name ?? node.title ?? node.slug ?? `#${node.id}`
}

/** Human-readable form of an edge type: INFLUENCES -> "influences". */
export function edgeLabel(type: string): string {
  return type.toLowerCase().replace(/_/g, " ")
}
